(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-blog-search-root]';
  var FORM_SELECTOR = '[data-blog-search-form]';
  var TOPIC_FILTER_SELECTOR = '[data-blog-topic-filter]';
  var STORAGE_PREFIX = 'milano.blogSearch.recent.v1.';

  function normalizeTerm(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  }

  function keyFor(term) {
    return normalizeTerm(term).toLocaleLowerCase();
  }

  function uniqueTerms(terms) {
    var seen = new Set();
    return terms.filter(function (term) {
      var normalized = normalizeTerm(term);
      var key = keyFor(normalized);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(normalizeTerm);
  }

  function splitLines(value) {
    return uniqueTerms(String(value || '').split(/\r?\n/));
  }

  function parseSynonyms(value) {
    return String(value || '').split(/\r?\n/).reduce(function (map, line) {
      var parts = line.split('=');
      if (parts.length < 2) return map;
      var alias = keyFor(parts.shift());
      var preferred = normalizeTerm(parts.join('='));
      if (alias && preferred) map.set(alias, preferred);
      return map;
    }, new Map());
  }

  function preferencesAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return true;
    return privacy.preferencesProcessingAllowed();
  }

  function analyticsAllowed() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return true;
    return privacy.analyticsProcessingAllowed();
  }

  function safeStorageRead(key) {
    if (!preferencesAllowed()) return [];
    try {
      var value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.map(normalizeTerm).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function safeStorageWrite(key, value) {
    if (!preferencesAllowed()) return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can be unavailable in private browsing or restricted contexts.
    }
  }

  function parseConfig(root) {
    var node = root.querySelector('[data-blog-search-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function BlogSearch(root) {
    this.root = root;
    this.form = root.matches(FORM_SELECTOR) ? root : root.querySelector(FORM_SELECTOR);
    this.input = this.form && this.form.querySelector('[data-blog-search-input]');
    this.clearButton = this.form && this.form.querySelector('[data-blog-search-clear]');
    this.popup = this.form && this.form.querySelector('[data-blog-search-suggestions]');
    this.config = parseConfig(this.form || root);
    this.popular = splitLines(this.config.popularTerms);
    this.synonyms = parseSynonyms(this.config.synonymHints);
    this.stopWords = new Set(splitLines(this.config.suggestionStopWords).map(keyFor));
    this.recentLimit = Math.max(1, Math.min(Number(this.config.recentLimit) || 5, 8));
    this.suggestionLimit = Math.max(3, Math.min(Number(this.config.suggestionLimit) || 6, 10));
    this.storageKey = STORAGE_PREFIX + (this.config.blogHandle || 'wig-talk-blog');
    this.activeOption = -1;
    this.options = [];

    if (!this.form || !this.input || !this.clearButton || !this.popup) return;
    this.bind();
    this.updateClearButton();
  }

  BlogSearch.prototype.isUseful = function (term) {
    var tokens = keyFor(term).split(/\s+/).filter(Boolean);
    return tokens.some(function (token) {
      return token.length > 1 && !this.stopWords.has(token);
    }, this);
  };

  BlogSearch.prototype.getRecent = function () {
    if (this.config.recentEnabled === false) return [];
    return safeStorageRead(this.storageKey).filter(this.isUseful.bind(this)).slice(0, this.recentLimit);
  };

  BlogSearch.prototype.saveRecent = function (term) {
    if (this.config.recentEnabled === false || !this.isUseful(term)) return;
    var updated = uniqueTerms([term].concat(this.getRecent())).slice(0, this.recentLimit);
    safeStorageWrite(this.storageKey, updated);
  };

  BlogSearch.prototype.track = function (term, source) {
    if (this.config.trackingEnabled === false || !analyticsAllowed()) return;
    var detail = {
      search_term: term,
      search_surface: 'blog',
      search_scope: 'article',
      search_source: source || 'typed',
      blog_handle: this.config.blogHandle || 'wig-talk-blog'
    };
    document.dispatchEvent(new CustomEvent('milano:blog-search', {detail: detail}));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({event: 'blog_search_submit'}, detail));
    }
  };

  BlogSearch.prototype.matchTerms = function (terms, query, excluded) {
    var normalizedQuery = keyFor(query);
    return terms.filter(function (term) {
      var key = keyFor(term);
      return key && !excluded.has(key) && (!normalizedQuery || key.indexOf(normalizedQuery) !== -1);
    });
  };

  BlogSearch.prototype.buildGroup = function (label, terms, source) {
    if (!terms.length) return null;
    var group = document.createElement('div');
    var heading = document.createElement('p');
    var list = document.createElement('ul');

    group.className = 'blog-search-suggestions__group';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', label);
    heading.className = 'blog-search-suggestions__heading';
    heading.textContent = label;
    list.className = 'blog-search-suggestions__list';

    terms.forEach(function (term) {
      var item = document.createElement('li');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'blog-search-suggestions__option';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.dataset.blogSearchSuggestion = term;
      button.dataset.blogSearchSource = source;
      button.textContent = term;
      item.appendChild(button);
      list.appendChild(item);
    });

    group.appendChild(heading);
    group.appendChild(list);
    return group;
  };

  BlogSearch.prototype.render = function () {
    var query = normalizeTerm(this.input.value);
    var recent = this.getRecent();
    var excluded = new Set();
    var fragment = document.createDocumentFragment();
    var listbox = document.createElement('div');
    var recentMatches = this.matchTerms(recent, query, excluded).slice(0, this.suggestionLimit);

    recentMatches.forEach(function (term) { excluded.add(keyFor(term)); });

    var preferred = this.synonyms.get(keyFor(query));
    var popularPool = uniqueTerms(preferred ? [preferred].concat(this.popular) : this.popular);
    var popularMatches = this.matchTerms(popularPool, query, excluded).slice(0, Math.max(0, this.suggestionLimit - recentMatches.length));
    var recentGroup = this.buildGroup(this.config.recentLabel || 'Recent blog searches', recentMatches, 'recent');
    var popularGroup = this.buildGroup(this.config.popularLabel || 'Popular blog searches', popularMatches, preferred ? 'synonym' : 'popular');

    listbox.className = 'blog-search-suggestions__listbox';
    listbox.setAttribute('role', 'listbox');
    if (recentGroup) listbox.appendChild(recentGroup);
    if (popularGroup) listbox.appendChild(popularGroup);
    fragment.appendChild(listbox);

    if (recent.length) {
      var clearRecent = document.createElement('button');
      clearRecent.type = 'button';
      clearRecent.className = 'blog-search-suggestions__clear-recent';
      clearRecent.dataset.blogSearchClearRecent = '';
      clearRecent.textContent = this.config.clearRecentLabel || 'Clear recent searches';
      fragment.appendChild(clearRecent);
    }

    this.popup.replaceChildren(fragment);
    this.options = Array.from(this.popup.querySelectorAll('[data-blog-search-suggestion]'));
    this.activeOption = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.popup.hidden = this.options.length === 0 && recent.length === 0;
    this.input.setAttribute('aria-expanded', String(!this.popup.hidden));
  };

  BlogSearch.prototype.close = function () {
    this.popup.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeOption = -1;
    this.options.forEach(function (option) { option.setAttribute('aria-selected', 'false'); });
  };

  BlogSearch.prototype.selectIndex = function (index) {
    if (!this.options.length) return;
    this.activeOption = (index + this.options.length) % this.options.length;
    this.options.forEach(function (option, optionIndex) {
      option.setAttribute('aria-selected', String(optionIndex === this.activeOption));
    }, this);
    var active = this.options[this.activeOption];
    if (!active.id) active.id = this.input.id + '--option-' + this.activeOption;
    this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({block: 'nearest'});
  };

  BlogSearch.prototype.choose = function (button) {
    this.input.value = normalizeTerm(button.dataset.blogSearchSuggestion);
    this.form.dataset.blogSearchSource = button.dataset.blogSearchSource || 'suggestion';
    this.updateClearButton();
    this.close();
    if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    else this.form.submit();
  };

  BlogSearch.prototype.clear = function () {
    this.input.value = '';
    this.form.dataset.blogSearchSource = 'typed';
    this.input.dispatchEvent(new Event('input', {bubbles: true}));
    this.input.focus();
  };

  BlogSearch.prototype.updateClearButton = function () {
    var empty = normalizeTerm(this.input.value).length === 0;
    this.clearButton.hidden = empty;
    this.clearButton.setAttribute('aria-hidden', String(empty));
  };

  BlogSearch.prototype.bind = function () {
    this.input.addEventListener('click', function (event) {
      // Prevent the theme's in-page search controller from redirecting this
      // article-only field into the mobile/header product search popdown.
      event.stopImmediatePropagation();
    }, true);

    this.input.addEventListener('focus', this.render.bind(this));
    this.input.addEventListener('input', function () {
      this.form.dataset.blogSearchSource = 'typed';
      this.updateClearButton();
      this.render();
    }.bind(this));

    this.input.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (this.popup.hidden) this.render();
        this.selectIndex(this.activeOption + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (this.popup.hidden) this.render();
        this.selectIndex(this.activeOption - 1);
      } else if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'Enter' && this.activeOption > -1) {
        event.preventDefault();
        this.choose(this.options[this.activeOption]);
      }
    }.bind(this));

    this.input.addEventListener('keyup', function (event) {
      // This component owns its listbox keyboard behavior. The parent theme
      // search-form handler is intentionally bypassed on blog-only inputs.
      event.stopPropagation();
    });

    this.clearButton.addEventListener('click', function (event) {
      event.preventDefault();
      this.clear();
    }.bind(this));

    this.popup.addEventListener('click', function (event) {
      event.stopPropagation();
      var option = event.target.closest('[data-blog-search-suggestion]');
      if (option) {
        this.choose(option);
        return;
      }
      if (event.target.closest('[data-blog-search-clear-recent]')) {
        safeStorageWrite(this.storageKey, []);
        this.render();
        this.input.focus();
      }
    }.bind(this));

    this.form.addEventListener('submit', function (event) {
      var term = normalizeTerm(this.input.value);
      if (!term) {
        event.preventDefault();
        this.clear();
        return;
      }
      this.input.value = term;
      this.saveRecent(term);
      this.track(term, this.form.dataset.blogSearchSource || 'typed');
    }.bind(this));

    document.addEventListener('click', function (event) {
      if (!this.root.contains(event.target)) this.close();
    }.bind(this));

    document.addEventListener('visitorConsentCollected', function () {
      if (document.activeElement === this.input) this.render();
    }.bind(this));
  };

  function BlogTopicFilter(form) {
    this.form = form;
    this.select = form.querySelector('[data-blog-topic-select]');
    this.submitButton = form.querySelector('[data-blog-topic-submit]');
    if (!this.select) return;
    this.bind();
    this.syncState();
  }

  BlogTopicFilter.prototype.destination = function () {
    var selectedOption = this.select.options[this.select.selectedIndex];
    if (!selectedOption || !selectedOption.value) return null;

    try {
      var destination = new URL(selectedOption.value, window.location.href);
      return destination.origin === window.location.origin ? destination : null;
    } catch (error) {
      return null;
    }
  };

  BlogTopicFilter.prototype.syncState = function () {
    if (!this.submitButton) return;
    var destination = this.destination();
    var currentPath = window.location.pathname.replace(/\/$/, '') || '/';
    var destinationPath = destination && (destination.pathname.replace(/\/$/, '') || '/');
    this.submitButton.disabled = !destination || destinationPath === currentPath;
  };

  BlogTopicFilter.prototype.track = function (topic, destinationUrl) {
    if (this.form.dataset.blogTopicTrackingEnabled === 'false' || !analyticsAllowed()) return;
    var detail = {
      blog_handle: this.form.dataset.blogHandle || 'wig-talk-blog',
      blog_topic: topic || 'All topics',
      destination_url: destinationUrl.pathname,
      filter_surface: 'blog'
    };
    document.dispatchEvent(new CustomEvent('milano:blog-topic-filter', {detail: detail}));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({event: 'blog_topic_filter_apply'}, detail));
    }
  };

  BlogTopicFilter.prototype.bind = function () {
    this.select.addEventListener('change', this.syncState.bind(this));

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      var selectedOption = this.select.options[this.select.selectedIndex];
      if (!selectedOption || !selectedOption.value) return;
      var destination = this.destination();
      if (!destination) return;
      this.track(selectedOption.textContent.trim(), destination);
      window.location.assign(destination.href);
    }.bind(this));
  };

  function init(container) {
    var scope = container || document;
    scope.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (root.dataset.blogSearchReady === 'true') return;
      root.dataset.blogSearchReady = 'true';
      new BlogSearch(root);
    });
    scope.querySelectorAll(TOPIC_FILTER_SELECTOR).forEach(function (form) {
      if (form.dataset.blogTopicFilterReady === 'true') return;
      form.dataset.blogTopicFilterReady = 'true';
      new BlogTopicFilter(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
